"use client";
import React, { createContext, useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  login as loginApi,
  logout as logoutApi,
  register as registerApi,
} from "../services/auth";
import api from "../services/api";
import { getSettings, clearSettingsCache } from "../services/settings";

interface AuthContextType {
  user: any;
  loading: boolean;
  login: (email: string, password: string, remember?: boolean) => Promise<void>;
  signup: (data: any) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (data: any) => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user is authenticated
  const isAuthenticated = !!user && !!localStorage.getItem("token");

  // Validate token on mount
  useEffect(() => {
    validateToken();
  }, []);

  const validateToken = async () => {
    const token = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");

    if (!token) {
      setLoading(false);
      return;
    }

    try {
      // Optimistically load stored user
      let currentUser = storedUser ? JSON.parse(storedUser) : null;
      if (currentUser) {
        setUser(currentUser);
      }

      // Fetch fresh settings from server to ensure consistency across devices/browsers
      const settingsResponse = await getSettings();

      if (settingsResponse?.data?.settings) {
        const settings = settingsResponse.data.settings;
        const currencies = settingsResponse.data.currencies || [];
        // Use loose equality to handle string/number differences
        const currency = currencies.find((c: any) => c.id == settings.currency_id);

        // If we have a user, update their company settings
        if (currentUser) {
          const updatedUser = {
            ...currentUser,
            company: {
              ...(currentUser.company || {}),
              ...settings,
              currency: currency || currentUser.company?.currency,
              currency_symbol: currency?.currency_symbol || currentUser.company?.currency_symbol,
              currency_code: currency?.currency_code || currentUser.company?.currency_code,
            },
          };
          console.log("Syncing user settings from server:", updatedUser);
          setUser(updatedUser);
          localStorage.setItem("user", JSON.stringify(updatedUser)); // Persist fresh data
        }
      }
    } catch (error) {
      console.error("Token validation/settings sync failed:", error);
      // If the token is invalid (e.g. 401), we might want to clear it,
      // but for network errors we should probably keep the local data.
      // Only clear if it's strictly an auth error, but for now let's be safe
      // and NOT wipe everything on a simple fetch error unless sure.

      // If we failed to fetch settings but have a stored user, we leave it be.
      // If we have NO user and failed... then maybe clear.
      if (!storedUser) {
        localStorage.removeItem("token");
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const updateUser = (data: any) => {
    setUser((prevUser: any) => {
      const newUser = { ...prevUser, ...data };
      localStorage.setItem("user", JSON.stringify(newUser));
      return newUser;
    });
  };

  const login = async (email: string, password: string, remember = true) => {
    try {
      const { user: loggedInUser, token } = await loginApi(
        email,
        password,
        remember
      );

      if (loggedInUser && token) {
        setUser(loggedInUser);
        localStorage.setItem("user", JSON.stringify(loggedInUser));
        localStorage.setItem("token", token);

        // Redirect to intended page or dashboard
        const from = location.state?.from?.pathname || "/dashboard";
        navigate(from, { replace: true });
      }
    } catch (error) {
      throw error;
    }
  };

  const signup = async (formData: any) => {
    try {
      const { user: registeredUser, token } = await registerApi(formData);

      if (registeredUser && token) {
        setUser(registeredUser);
        localStorage.setItem("user", JSON.stringify(registeredUser));
        localStorage.setItem("token", token);
        navigate("/dashboard", { replace: true });
      }
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await logoutApi();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setUser(null);
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      clearSettingsCache();
      navigate("/login", { replace: true });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        signup,
        logout,
        updateUser,
        isAuthenticated,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
